import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  Dimensions,
  EmitterSubscription,
  Animated,
  Pressable,
  PanResponder,
  PanResponderInstance,
  GestureResponderEvent,
  PanResponderGestureState,
} from 'react-native';
import { getItemAsync, setItemAsync } from "expo-secure-store";
import { connect } from 'react-redux';

import HomeList from '../components/home/HomeList.component';
import MyStartedThoughtsList from '../components/home/MyStartedThoughtsList.component';
import Button from "../components/Button.component";
import SvgProfile from '../components/svg/SvgProfile.component';
import { User } from '../models/User';

import screenStyle from '../styles/screen.style';
import colorStyle from '../styles/color.style';

const THOT_WIDTH = 134;
const HEADER_HEIGHT = 56;
const FOOTER_HEIGHT = 48;
const TITLE_LINE_HEIGHT = 14;
const TITLE_PADDING_Y = 7;
const COMPACT_TITLE_HEIGHT = TITLE_LINE_HEIGHT + TITLE_PADDING_Y * 2;
const TAG_HEIGHT = 19;
const COLLAPSED_SECTION_HEIGHT = TAG_HEIGHT + 8;
const HOME_TOUCH_SENSITIVITY = 6;
const HOME_SCROLL_DECAY = 220;
const HOME_COLLAPSE_STORAGE_KEY = "home_collapsed_sections";
const getImageHeight = () => {
  const screen = Dimensions.get("window");
  return Math.round(THOT_WIDTH * ((screen.height - HEADER_HEIGHT) / Math.max(screen.width, 1)));
};

const getSliderHeight = () => COMPACT_TITLE_HEIGHT + getImageHeight() + FOOTER_HEIGHT;
const getTagTop = () => Math.max(0, COMPACT_TITLE_HEIGHT + getImageHeight() / 2 - TAG_HEIGHT / 2);
const getViewportHeight = () => Dimensions.get("window").height;

type Props = {
  navigation: any;
  friends: { [_id: string]: User };
};

type State = {
  viewportVersion: number;
  friendsCollapsed: boolean;
  friendsChainsCollapsed: boolean;
  myThoughtsCollapsed: boolean;
  startedCollapsed: boolean;
};

class Home extends React.Component<Props, State> {
  _dimensionsSubscription?: EmitterSubscription;
  _panResponder: PanResponderInstance;
  _viewportRef: any;
  _friendsProgress = new Animated.Value(1);
  _friendsChainsProgress = new Animated.Value(1);
  _myThoughtsProgress = new Animated.Value(1);
  _startedProgress = new Animated.Value(1);
  _scrollY = new Animated.Value(0);
  _scrollOffsetY = 0;
  _scrollTargetY = 0;
  _scrollStartY = 0;
  _contentHeight = 0;

  state: State = {
    viewportVersion: 0,
    friendsCollapsed: false,
    friendsChainsCollapsed: false,
    myThoughtsCollapsed: false,
    startedCollapsed: false,
  };

  constructor(props) {
    super(props);

    this._panResponder = PanResponder.create({
      onMoveShouldSetPanResponderCapture: this._onMoveShouldSetPanResponder,
      onMoveShouldSetPanResponder: this._onMoveShouldSetPanResponder,
      onPanResponderGrant: this._onPanResponderGrant,
      onPanResponderMove: this._onPanResponderMove,
      onPanResponderRelease: this._onPanResponderRelease,
      onPanResponderTerminate: this._onPanResponderRelease,
    });
  }

  componentDidMount() {
    this._dimensionsSubscription = Dimensions.addEventListener("change", this._onDimensionsChange);
    this._scrollY.addListener(({ value }) => {
      this._scrollOffsetY = value;
    });
    this._loadCollapsedState();
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const collapsedStateChanged =
      prevState.friendsCollapsed !== this.state.friendsCollapsed ||
      prevState.friendsChainsCollapsed !== this.state.friendsChainsCollapsed ||
      prevState.myThoughtsCollapsed !== this.state.myThoughtsCollapsed ||
      prevState.startedCollapsed !== this.state.startedCollapsed;

    if (
      prevState.viewportVersion !== this.state.viewportVersion ||
      collapsedStateChanged
    ) {
      this._animateScrollTo(this._scrollTargetY, true);
    }

    if (collapsedStateChanged) {
      this._saveCollapsedState();
    }
  }

  componentWillUnmount() {
    this._dimensionsSubscription?.remove();
    this._scrollY.removeAllListeners();
    this._detachWheelListener();
  }

  _onDimensionsChange = () => {
    this.setState(({ viewportVersion }) => ({ viewportVersion: viewportVersion + 1 }));
  };

  _newThot = (): void => {
    this.props.navigation.navigate("NewThot", { addThot: this._addThot });
  };

  _goToProfile = () => {
    this.props.navigation.navigate("Settings");
  };

  _getCollapsedState = () => ({
    friendsCollapsed: this.state.friendsCollapsed,
    friendsChainsCollapsed: this.state.friendsChainsCollapsed,
    myThoughtsCollapsed: this.state.myThoughtsCollapsed,
    startedCollapsed: this.state.startedCollapsed,
  });

  _loadCollapsedState = async () => {
    try {
      const raw =
        Platform.OS === "web"
          ? window.localStorage.getItem(HOME_COLLAPSE_STORAGE_KEY)
          : await getItemAsync(HOME_COLLAPSE_STORAGE_KEY);
      if (!raw) return;

      const saved = JSON.parse(raw);
      const nextState = {
        friendsCollapsed: !!saved.friendsCollapsed,
        friendsChainsCollapsed: !!saved.friendsChainsCollapsed,
        myThoughtsCollapsed: !!saved.myThoughtsCollapsed,
        startedCollapsed: !!saved.startedCollapsed,
      };

      this._friendsProgress.setValue(nextState.friendsCollapsed ? 0 : 1);
      this._friendsChainsProgress.setValue(nextState.friendsChainsCollapsed ? 0 : 1);
      this._myThoughtsProgress.setValue(nextState.myThoughtsCollapsed ? 0 : 1);
      this._startedProgress.setValue(nextState.startedCollapsed ? 0 : 1);
      this.setState(nextState);
    } catch (err) {
      console.log("Error > Home > load collapsed state:", err);
    }
  };

  _saveCollapsedState = async () => {
    try {
      const raw = JSON.stringify(this._getCollapsedState());
      if (Platform.OS === "web") {
        window.localStorage.setItem(HOME_COLLAPSE_STORAGE_KEY, raw);
      } else {
        await setItemAsync(HOME_COLLAPSE_STORAGE_KEY, raw);
      }
    } catch (err) {
      console.log("Error > Home > save collapsed state:", err);
    }
  };

  _toggleSection = (section: "friends" | "friendsChains" | "myThoughts" | "started") => {
    const collapsedKey =
      section === "friends"
        ? "friendsCollapsed"
        : section === "friendsChains"
          ? "friendsChainsCollapsed"
          : section === "myThoughts"
            ? "myThoughtsCollapsed"
            : "startedCollapsed";
    const progress =
      section === "friends"
        ? this._friendsProgress
        : section === "friendsChains"
          ? this._friendsChainsProgress
          : section === "myThoughts"
            ? this._myThoughtsProgress
            : this._startedProgress;
    const nextCollapsed = !this.state[collapsedKey];

    this.setState({ [collapsedKey]: nextCollapsed } as Pick<State, keyof State>);
    Animated.spring(progress, {
      toValue: nextCollapsed ? 0 : 1,
      stiffness: nextCollapsed ? 240 : 210,
      damping: nextCollapsed ? 30 : 24,
      mass: 0.8,
      overshootClamping: nextCollapsed,
      restDisplacementThreshold: 0.01,
      restSpeedThreshold: 0.01,
      useNativeDriver: false,
    }).start();
  };

  _getMinScrollY = () => Math.min(0, getViewportHeight() - this._contentHeight - 20);

  _clampScrollY = (value: number) => {
    const min = this._getMinScrollY();
    if (value > 0) return 0;
    if (value < min) return min;
    return value;
  };

  _animateScrollTo = (value: number, immediate = false) => {
    const next = this._clampScrollY(value);
    this._scrollTargetY = next;
    this._scrollY.stopAnimation();
    if (immediate) {
      this._scrollY.setValue(next);
      return;
    }

    Animated.timing(this._scrollY, {
      toValue: next,
      duration: 180,
      useNativeDriver: true,
    }).start();
  };

  _onMoveShouldSetPanResponder = (
    evt: GestureResponderEvent,
    gestureState: PanResponderGestureState
  ) => {
    const absDx = Math.abs(gestureState.dx);
    const absDy = Math.abs(gestureState.dy);
    return absDy > HOME_TOUCH_SENSITIVITY && absDy > absDx * 1.2;
  };

  _onPanResponderGrant = () => {
    this._scrollY.stopAnimation();
    this._scrollStartY = this._scrollOffsetY;
  };

  _onPanResponderMove = (
    evt: GestureResponderEvent,
    gestureState: PanResponderGestureState
  ) => {
    const next = this._clampScrollY(this._scrollStartY + gestureState.dy);
    this._scrollY.setValue(next);
    this._scrollTargetY = next;
  };

  _onPanResponderRelease = (
    evt: GestureResponderEvent,
    gestureState: PanResponderGestureState
  ) => {
    const next = this._scrollTargetY + gestureState.vy * (HOME_SCROLL_DECAY * 2.2);
    this._animateScrollTo(next);
  };

  _handleWheel = (evt) => {
    const deltaY = evt?.deltaY ?? evt?.nativeEvent?.deltaY ?? 0;
    const next = this._scrollTargetY - deltaY * 2.4;
    this._animateScrollTo(next);
  };

  _detachWheelListener = () => {
    if (Platform.OS !== "web" || !this._viewportRef?.removeEventListener) return;
    this._viewportRef.removeEventListener("wheel", this._handleWheel);
  };

  _setViewportRef = (ref) => {
    if (this._viewportRef === ref) return;
    this._detachWheelListener();
    this._viewportRef = ref;
  };

  _onContentLayout = (evt) => {
    const measuredHeight = evt?.nativeEvent?.layout?.height || 0;
    if (measuredHeight > this._contentHeight) this._contentHeight = measuredHeight;
    this._animateScrollTo(this._scrollTargetY, true);
  };

  _getTotalContentHeight = (sliderHeight: number) => {
    const sectionHeights = [
      this.state.friendsCollapsed ? COLLAPSED_SECTION_HEIGHT : sliderHeight,
      this.state.friendsChainsCollapsed ? COLLAPSED_SECTION_HEIGHT : sliderHeight,
      this.state.myThoughtsCollapsed ? COLLAPSED_SECTION_HEIGHT : sliderHeight,
      this.state.startedCollapsed ? COLLAPSED_SECTION_HEIGHT : sliderHeight,
    ];
    return (
      20 +
      110 +
      sectionHeights.reduce((sum, height, index) => sum + height + (index > 0 ? 18 : 0), 0)
    );
  };

  _renderSection = (
    label: string,
    section: "friends" | "friendsChains" | "myThoughts" | "started",
    sliderHeight: number,
    tagTop: number,
    child: React.ReactNode,
    extraStyle?: any
  ) => {
    const collapsed =
      section === "friends"
        ? this.state.friendsCollapsed
        : section === "friendsChains"
          ? this.state.friendsChainsCollapsed
          : section === "myThoughts"
            ? this.state.myThoughtsCollapsed
            : this.state.startedCollapsed;
    const progress =
      section === "friends"
        ? this._friendsProgress
        : section === "friendsChains"
          ? this._friendsChainsProgress
          : section === "myThoughts"
            ? this._myThoughtsProgress
            : this._startedProgress;
    const animatedHeight = progress.interpolate({
      inputRange: [0, 1],
      outputRange: [COLLAPSED_SECTION_HEIGHT, sliderHeight],
    });
    const animatedTagTop = progress.interpolate({
      inputRange: [0, 1],
      outputRange: [(COLLAPSED_SECTION_HEIGHT - TAG_HEIGHT) / 2, tagTop],
    });
    const animatedContentStyle = {
      opacity: progress,
      transform: [
        {
          translateX: progress.interpolate({
            inputRange: [0, 1],
            outputRange: [240, 0],
          }),
        },
        {
          translateY: progress.interpolate({
            inputRange: [0, 1],
            outputRange: [-10, 0],
          }),
        },
        {
          scale: progress.interpolate({
            inputRange: [0, 1],
            outputRange: [0.94, 1],
          }),
        },
      ],
    };

    return (
      <Animated.View style={[styles.section, extraStyle, { height: animatedHeight }]}>
        <Animated.View
          pointerEvents={collapsed ? "none" : "auto"}
          style={[styles.sectionContent, animatedContentStyle]}
        >
          {child}
        </Animated.View>
        <Animated.View style={[styles.sectionTitleWrap, { top: animatedTagTop }]}>
          <Pressable style={styles.sectionTitle} onPress={() => this._toggleSection(section)}>
            <Text selectable={false} style={styles.sectionTitleText}>{label}</Text>
            {!collapsed && <Text selectable={false} style={styles.sectionTitleArrow}>✕</Text>}
          </Pressable>
        </Animated.View>
      </Animated.View>
    );
  };
  
  render() {
    const sliderHeight = getSliderHeight();
    const tagTop = getTagTop();
    const friendKey = Object.keys(this.props.friends || {}).sort().join("-");
    this._contentHeight = this._getTotalContentHeight(sliderHeight);

    return (
      <View style={[screenStyle.container, styles.container]}>
        <View
          ref={this._setViewportRef}
          style={styles.scrollViewport}
          onWheel={Platform.OS === "web" ? this._handleWheel : undefined}
          {...this._panResponder.panHandlers}
        >
          <Animated.View
            onLayout={this._onContentLayout}
            style={[styles.content, { transform: [{ translateY: this._scrollY }] }]}
          >
            {this._renderSection(
              "friends thoughts",
              "friends",
              sliderHeight,
              tagTop,
              <HomeList key={`home-${friendKey}`} />
            )}

            {this._renderSection(
              "friends thought chains",
              "friendsChains",
              sliderHeight,
              tagTop,
              <HomeList key={`friends-chains-${friendKey}`} showRoots />,
              styles.startedSection
            )}

            {this._renderSection(
              "my thoughts",
              "myThoughts",
              sliderHeight,
              tagTop,
              <MyStartedThoughtsList key={`my-thoughts-${friendKey}`} onlyRoots={false} />,
              styles.startedSection
            )}

            {this._renderSection(
              "my thought chains",
              "started",
              sliderHeight,
              tagTop,
              <MyStartedThoughtsList key={`started-${friendKey}`} />,
              styles.startedSection
            )}
          </Animated.View>
        </View>

        <View style={styles.bottomBar}>
          <View pointerEvents="none" style={styles.bottomGradient} />
          <Pressable style={styles.profileButton} onPress={this._goToProfile}>
            <SvgProfile />
          </Pressable>
          <Button
            style={styles.newThoughtButton}
            title="new Thought"
            onPress={this._newThot}
          />
        </View>
      </View>
    );
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 0,
    overflow: 'visible',
    ...(Platform.OS === 'web' ? ({ userSelect: 'none', WebkitUserSelect: 'none' } as any) : {}),
  },
  scrollViewport: {
    flex: 1,
    overflow: 'hidden',
  },
  content: {
    paddingTop: 20,
    paddingBottom: 110,
  },
  section: {
    position: 'relative',
    overflow: 'hidden',
  },
  sectionContent: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
  },
  startedSection: {
    marginTop: 18,
  },
  bottomBar: {
    position: 'absolute',
    left: 20,
    right: 20,
    bottom: 20,
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: 12,
    backgroundColor: 'transparent',
    zIndex: 20,
    elevation: 20,
    ...(Platform.OS === 'web'
      ? ({
          position: 'fixed',
          zIndex: 999,
        } as any)
      : {}),
  },
  bottomGradient: {
    position: 'absolute',
    left: -20,
    right: -20,
    bottom: -20,
    height: 120,
    ...(Platform.OS === 'web'
      ? ({
          backgroundImage: `linear-gradient(to bottom, rgba(237,235,234,0) 0%, ${colorStyle.appBg} 55%, ${colorStyle.appBg} 100%)`,
        } as any)
      : {
          backgroundColor: 'rgba(237,235,234,0.85)',
          opacity: 0.9,
        }),
  },
  profileButton: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#fff',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  newThoughtButton: {
    flex: 1,
    width: 'auto',
    marginTop: 0,
    marginBottom: 0,
  },
  sectionTitleWrap: {
    position: 'absolute',
    right: 20,
    zIndex: 5,
  },
  sectionTitle: {
    flexDirection: 'row',
    alignItems: 'center',
    fontWeight: '500',
    backgroundColor: '#fff',
    paddingHorizontal: 9,
    height: TAG_HEIGHT,
    borderRadius: 999,
    overflow: 'hidden',
  },
  sectionTitleText: {
    fontWeight: '500',
    fontSize: 8.4,
    textTransform: 'lowercase',
    lineHeight: TAG_HEIGHT,
  },
  sectionTitleArrow: {
    marginLeft: 4,
    fontSize: 8.4,
    lineHeight: TAG_HEIGHT,
  },
});

const mapStateToProps = ({ friends }) => ({ friends });

export default connect(mapStateToProps, null)(Home);
